import React, { useState } from "react";
import { X, AlertCircle } from "lucide-react";
import { CORE_STYLES } from "../../utils/beerTaxonomy";
import Dropdown from "./dropdown";

const YELLOW_INPUT_CLASS =
  "w-full p-2.5 bg-[#fef5d4] text-gray-800 border border-[#eed98b] rounded focus:outline-none text-left";


const ATTRIBUTE_FIELDS = [
  { key: "is_vegan", label: "Vegan" },
  { key: "has_gluten", label: "Contains Gluten" },
  { key: "has_lactose", label: "Contains Dairy / Lactose" },
  { key: "has_nuts", label: "Contains Nuts" },
  { key: "is_filtered", label: "Filtered" },
  { key: "seasonal", label: "Seasonal" },
  { key: "limited", label: "Limited Release" },
];

const FORMAT_LABELS = { draft: "Draft", can: "Can", bottle: "Bottle" };

// Serving & Price actions are encoded as "format:<draft|can|bottle>:<update|remove>"
// so the UI can offer 6 discrete actions while still mapping onto the single
// backend "Update Formats" action + a per-format payload.
const parseFormatAction = (key) => {
  const [, formatKey, mode] = key.split(":");
  return { formatKey, mode };
};

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-black text-[#881337] uppercase tracking-wider mb-1">
    {children}
  </p>
);

const InfoBanner = ({ tone = "green", children }) => {
  const tones = {
    green: "bg-green-50 border-green-200 text-green-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <div className={`border rounded-lg px-3 py-2.5 text-sm mb-4 ${tones[tone]}`}>
      {children}
    </div>
  );
};

const ActionButton = ({ label, onClick, danger }) => (
  <button
    onClick={onClick}
    className={`w-full text-left p-3 rounded-lg border transition-all group ${
      danger
        ? "border-red-200 text-red-600 hover:bg-red-50"
        : "border-gray-200 hover:border-[#881337]/30 hover:bg-[#881337]/5"
    }`}
  >
    <span
      className={`text-sm font-semibold ${
        danger ? "text-red-600" : "text-gray-700 group-hover:text-[#881337]"
      }`}
    >
      {label}
    </span>
  </button>
);

const defaultAttributesValue = () =>
  ATTRIBUTE_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: false }), {});

const initialValueFor = (actionKey) => {
  if (actionKey.startsWith("format:")) {
    const { mode } = parseFormatAction(actionKey);
    return mode === "update" ? { serving_size: "", price: "" } : null;
  }
  switch (actionKey) {
    case "Add Notes":
    case "Replace Notes":
    case "Add Beer Description":
    case "Replace Beer Description":
    case "Change Style":
      return "";
    case "Update Attributes":
      return defaultAttributesValue();
    default:
      return null;
  }
};

const BeerBulkActionModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedCount,
  selectedBeers = [],
  previousTable,
}) => {
  const categories = previousTable
    ? [
        {
          name: "Status",
          heading: "Previous-menu restore action",
          subtitle:
            "The Previous Manage Menu provides one bulk action: restore selected beers to the current menu.",
          infoBanner: {
            tone: "blue",
            text: "Restored beers become active immediately, matching the existing food and wine behavior.",
          },
          sections: [
            {
              actions: [{ value: "Restore Beers", label: "Restore to Current Menu" }],
            },
          ],
        },
      ]
    : [
        {
          name: "Status",
          heading: "Current-menu status actions",
          subtitle: "Archive is available only while viewing the Current Manage Menu.",
          infoBanner: { tone: "green", text: "Shows Active, Inactive, and Archive actions." },
          sections: [
            {
              actions: [
                { value: "Set Status → Active", label: "Set Status → Active" },
                { value: "Set Status → Inactive", label: "Set Status → Inactive" },
                { value: "Archive Beers", label: "Archive Beers" },
              ],
            },
          ],
        },
        {
          name: "Classification",
          heading: "Classification & Style",
          subtitle: "Use controlled Beer Taxonomy values.",
          sections: [{ actions: [{ value: "Change Style", label: "Change Style" }] }],
        },
        {
          name: "Attributes",
          heading: "Characteristics & Availability",
          subtitle: "Set shared Boolean and origin-classification values.",
          sections: [{ actions: [{ value: "Update Attributes", label: "Update Attributes" }] }],
        },
        {
          name: "Serving & Price",
          heading: "Serving Formats & Pricing",
          subtitle: "Keep format, serving size, and price together.",
          sections: [
            {
              actions: [
                { value: "format:draft:update", label: "Add or Update Draft" },
                { value: "format:can:update", label: "Add or Update Can" },
                { value: "format:bottle:update", label: "Add or Update Bottle" },
                { value: "format:draft:remove", label: "Remove Draft", danger: true },
                { value: "format:can:remove", label: "Remove Can", danger: true },
                { value: "format:bottle:remove", label: "Remove Bottle", danger: true },
              ],
            },
          ],
        },
        {
          name: "Content",
          heading: "Descriptions & Training Notes",
          subtitle: "Keep guest-facing and internal content separate.",
          sections: [
            {
              eyebrow: "GUEST-FACING",
              heading: "Beer Description",
              subtitle: "Menu description actions.",
              actions: [
                { value: "Add Beer Description", label: "Add Beer Description" },
                { value: "Replace Beer Description", label: "Replace Beer Description" },
                { value: "Clear Beer Description", label: "Clear Beer Description", danger: true },
              ],
            },
            {
              heading: "Training Notes",
              subtitle: "Internal service, rotation, or training guidance.",
              actions: [
                { value: "Add Notes", label: "Add Training Notes" },
                { value: "Replace Notes", label: "Replace Training Notes" },
                { value: "Clear Notes", label: "Clear Training Notes", danger: true },
              ],
            },
          ],
        },
      ];

  const [activeCategory, setActiveCategory] = useState(categories[0].name);
  const [selectedAction, setSelectedAction] = useState("");
  const [actionValue, setActionValue] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const activeCategoryData = categories.find((c) => c.name === activeCategory);
  const allActions = categories.flatMap((c) => c.sections.flatMap((s) => s.actions));
  const findAction = (value) => allActions.find((a) => a.value === value);
  const actionLabel = (value) => findAction(value)?.label || value;

  const handleActionSelect = (action) => {
    setSelectedAction(action);
    setActionValue(initialValueFor(action));
    setIsConfirming(false);
  };

  const handleCategorySelect = (cat) => {
    setActiveCategory(cat.name);
    setIsConfirming(false);
    const totalActions = cat.sections.reduce((n, s) => n + s.actions.length, 0);
    if (totalActions === 1) {
      handleActionSelect(cat.sections[0].actions[0].value);
    } else {
      setSelectedAction("");
      setActionValue(null);
    }
  };

  // Translates the modal's internal selection into the real backend
  // action/value pair expected by bulkUpdateBeers.
  const resolveBackendCall = () => {
    if (selectedAction.startsWith("format:")) {
      const { formatKey, mode } = parseFormatAction(selectedAction);
      const value =
        mode === "remove"
          ? { [formatKey]: { mode: "remove" } }
          : {
              [formatKey]: {
                mode: "update",
                serving_size: actionValue?.serving_size,
                price: actionValue?.price,
              },
            };
      return { action: "Update Formats", value };
    }
    return { action: selectedAction, value: actionValue };
  };

  const handleConfirm = async () => {
    if (!isConfirming) {
      setIsConfirming(true);
    } else {
      setIsLoading(true);
      try {
        const { action, value } = resolveBackendCall();
        await onConfirm(action, value);
        onClose();
      } catch (error) {
        console.error("Bulk action failed:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const isApplyDisabled = () => {
    if (!selectedAction) return true;
    if (selectedAction === "Change Style" && !actionValue) return true;
    if (
      (selectedAction === "Replace Notes" || selectedAction === "Replace Beer Description") &&
      (actionValue === null || actionValue === undefined)
    )
      return true;
    if (selectedAction.startsWith("format:")) {
      const { mode } = parseFormatAction(selectedAction);
      if (mode === "update") {
        const price = parseFloat(actionValue?.price);
        if (!actionValue?.serving_size?.trim() || isNaN(price) || price < 0) return true;
      }
    }
    return false;
  };

  const getChangeSummaryLines = () => {
    if (selectedAction.startsWith("format:")) {
      const { formatKey, mode } = parseFormatAction(selectedAction);
      if (mode === "remove") return [`${FORMAT_LABELS[formatKey]}: Remove format`];
      const price = parseFloat(actionValue?.price);
      return [
        `${FORMAT_LABELS[formatKey]}: Set to ${actionValue?.serving_size} / $${
          isNaN(price) ? "0.00" : price.toFixed(2)
        }`,
      ];
    }
    switch (selectedAction) {
      case "Set Status → Active":
        return ["Set status to Active"];
      case "Set Status → Inactive":
        return ["Set status to Inactive"];
      case "Archive Beers":
        return ["Archive the selected beers (move to Previous Menu)"];
      case "Restore Beers":
        return ["Restore the selected beers to the Current Menu"];
      case "Change Style":
        return [`Change Core Style to "${actionValue}"`];
      case "Add Notes":
        return [`Append training note: "${actionValue}"`];
      case "Replace Notes":
        return [
          actionValue
            ? `Replace training notes with: "${actionValue}"`
            : "Replace training notes with a blank value",
        ];
      case "Clear Notes":
        return ["Clear all training notes"];
      case "Add Beer Description":
        return [`Append to beer description: "${actionValue}"`];
      case "Replace Beer Description":
        return [
          actionValue
            ? `Replace beer description with: "${actionValue}"`
            : "Replace beer description with a blank value",
        ];
      case "Clear Beer Description":
        return ["Clear the beer description"];
      case "Update Attributes":
        return ATTRIBUTE_FIELDS.map(
          ({ key, label }) => `${label}: ${actionValue?.[key] ? "Yes" : "No"}`
        );
      default:
        return [selectedAction];
    }
  };

  const renderActionInput = () => {
    if (selectedAction.startsWith("format:")) {
      const { formatKey, mode } = parseFormatAction(selectedAction);
      if (mode === "remove") {
        return (
          <p className="text-xs text-red-600 mt-4">
            The {FORMAT_LABELS[formatKey]} format will be removed from every selected beer.
          </p>
        );
      }
      return (
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Format</label>
            <select
              value={formatKey}
              onChange={(e) =>
                setSelectedAction(`format:${e.target.value}:update`)
              }
              className={YELLOW_INPUT_CLASS}
            >
              {Object.entries(FORMAT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">
                Serving Size
              </label>
              <input
                type="text"
                placeholder="e.g. 16 oz"
                className={YELLOW_INPUT_CLASS}
                value={actionValue?.serving_size || ""}
                onChange={(e) =>
                  setActionValue((prev) => ({ ...prev, serving_size: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Price</label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                className={YELLOW_INPUT_CLASS}
                value={actionValue?.price ?? ""}
                onChange={(e) =>
                  setActionValue((prev) => ({ ...prev, price: e.target.value }))
                }
              />
            </div>
          </div>
        </div>
      );
    }

    switch (selectedAction) {
      case "Change Style":
        return (
          <div className="mt-4">
            <label className="text-sm font-bold text-gray-700 block mb-1">Beer Style*</label>
            <Dropdown
              label="Select a Beer Taxonomy style"
              options={CORE_STYLES.map((style) => ({ value: style, label: style }))}
              selectedOption={actionValue || ""}
              onSelect={(option) => setActionValue(option.value)}
              searchable
              searchPlaceholder="Search beer styles..."
              className="w-full"
              buttonClassName="w-full justify-between text-left bg-[#fef5d4] border border-[#eed98b]"
            />
            <p className="text-xs text-gray-500 mt-2">
              Automatically updates Style Group, Body, Texture, and Flavor Intensity.
            </p>
            <div className="mt-3 border border-green-200 bg-green-50 rounded-lg px-3 py-2.5">
              <p className="text-sm font-bold text-green-800">Derived automatically</p>
              <p className="text-xs text-green-700 mt-0.5">
                Style Group · Body · Texture · Flavor Intensity
              </p>
            </div>
          </div>
        );
      case "Add Notes":
      case "Replace Notes":
      case "Add Beer Description":
      case "Replace Beer Description":
        return (
          <textarea
            className="w-full p-2 border border-gray-300 rounded-md mt-4 h-24"
            placeholder="Enter text..."
            value={actionValue || ""}
            onChange={(e) => setActionValue(e.target.value)}
          />
        );
      case "Update Attributes":
        return (
          <div className="mt-4">
            <p className="text-xs text-gray-500 mb-3">
              Selected means Active/Yes. Unselected means Inactive/No.
            </p>
            <p className="text-sm font-bold text-gray-800 mb-2">Characteristics & availability</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ATTRIBUTE_FIELDS.map(({ key, label }) => (
                <label
                  key={key}
                  className="flex items-center gap-2 p-2.5 border border-gray-200 rounded-lg cursor-pointer hover:border-[#881337]/40 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={!!actionValue?.[key]}
                    onChange={(e) =>
                      setActionValue((prev) => ({ ...prev, [key]: e.target.checked }))
                    }
                    className="h-4 w-4 rounded border-gray-300 text-[#881337] focus:ring-[#881337]"
                  />
                  <span className="text-sm font-semibold text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const summaryLines = isConfirming ? getChangeSummaryLines() : [];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
          <div>
            <h2 className="text-xl font-bold text-[#881337]">
              {isConfirming
                ? `Apply Changes to ${selectedCount} Beer${selectedCount === 1 ? "" : "s"}?`
                : `Beer Bulk Actions — ${previousTable ? "Previous" : "Current"} Manage Menu`}
            </h2>
            <p className="text-sm text-gray-500 font-medium">
              {isConfirming
                ? "Confirm the affected beers and pending change."
                : `${selectedCount} beers selected • ${previousTable ? "Previous" : "Current"} Manage Menu`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        {isConfirming ? (
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <Eyebrow>Final Confirmation</Eyebrow>
            <h3 className="text-lg font-bold text-gray-900 -mt-2">
              Apply changes to {selectedCount} beer{selectedCount === 1 ? "" : "s"}?
            </h3>
            <p className="text-sm text-gray-500 -mt-3">
              Check the pending update and affected beers before applying it.
            </p>

            <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl flex gap-3">
              <AlertCircle className="text-orange-500 shrink-0" size={24} />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-black text-orange-600 uppercase tracking-wider mb-1">
                  Pending Change
                </p>
                <h4 className="font-bold text-orange-900 mb-1">
                  {activeCategoryData?.name} → {actionLabel(selectedAction)}
                </h4>
                {summaryLines.length > 1 ? (
                  <ul className="text-sm text-orange-800 list-disc list-inside space-y-0.5">
                    {summaryLines.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-orange-800">{summaryLines[0]}</p>
                )}
              </div>
            </div>

            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                <span className="text-[11px] font-black text-gray-500 uppercase tracking-wider">
                  Affected Beers
                </span>
                <span className="text-xs font-bold text-[#881337]">
                  {selectedCount} selected
                </span>
              </div>
              <ul className="max-h-56 overflow-y-auto divide-y divide-gray-100">
                {selectedBeers.length > 0 ? (
                  selectedBeers.map((beer, i) => (
                    <li key={beer.uuid || i} className="flex items-center gap-3 px-4 py-2.5">
                      <span className="w-6 h-6 rounded-full bg-[#881337]/10 text-[#881337] text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">
                          {beer.product_name || "Untitled beer"}
                        </p>
                        {beer.brewery_name && (
                          <p className="text-xs text-gray-500 truncate">{beer.brewery_name}</p>
                        )}
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="px-4 py-3 text-sm text-gray-400">
                    {selectedCount} beer{selectedCount === 1 ? "" : "s"} selected
                  </li>
                )}
              </ul>
            </div>

            <div className="border border-red-200 bg-red-50 text-red-700 rounded-lg px-3 py-2.5 text-sm">
              This action may overwrite existing values and cannot be easily undone.
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto flex">
            {/* Categories Sidebar */}
            <div className="w-48 bg-gray-50 border-r border-gray-100 flex-shrink-0">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategorySelect(cat)}
                  className={`w-full text-left px-4 py-3 text-sm font-semibold transition-colors ${
                    activeCategory === cat.name
                      ? "bg-white text-[#881337] border-r-2 border-[#881337]"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Actions Pane */}
            <div className="flex-1 p-6 space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {activeCategoryData?.heading}
                </h3>
                {activeCategoryData?.subtitle && (
                  <p className="text-sm text-gray-500 mt-0.5">{activeCategoryData.subtitle}</p>
                )}
              </div>

              {!selectedAction ? (
                <>
                  {activeCategoryData?.infoBanner && (
                    <InfoBanner tone={activeCategoryData.infoBanner.tone}>
                      {activeCategoryData.infoBanner.text}
                    </InfoBanner>
                  )}
                  {activeCategoryData?.sections.map((section, sIdx) => (
                    <div key={sIdx} className="space-y-2">
                      {section.eyebrow && <Eyebrow>{section.eyebrow}</Eyebrow>}
                      {section.heading && (
                        <h4 className="text-sm font-bold text-gray-800 -mt-1">
                          {section.heading}
                        </h4>
                      )}
                      {section.subtitle && (
                        <p className="text-xs text-gray-500 -mt-1 mb-2">{section.subtitle}</p>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {section.actions.map((action) => (
                          <ActionButton
                            key={action.value}
                            label={action.label}
                            danger={action.danger}
                            onClick={() => handleActionSelect(action.value)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="space-y-4">
                  {allActions.length > 1 && (
                    <button
                      onClick={() => {
                        setSelectedAction("");
                        setActionValue(null);
                      }}
                      className="text-xs font-bold text-[#881337] hover:underline flex items-center gap-1"
                    >
                      ← Back to {activeCategory}
                    </button>
                  )}
                  <h4 className="text-base font-bold text-gray-900">
                    {actionLabel(selectedAction)}
                  </h4>

                  {renderActionInput()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
          {isConfirming ? (
            <>
              <button
                onClick={() => setIsConfirming(false)}
                disabled={isLoading}
                className="px-6 py-2 rounded-lg font-bold text-sm text-gray-500 hover:bg-gray-100 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={isLoading}
                className="px-8 py-2 rounded-lg font-bold text-sm shadow-md transition-all bg-[#881337] text-white hover:bg-[#70102d]"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Applying...</span>
                  </div>
                ) : (
                  "Confirm & Apply"
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-6 py-2 rounded-lg font-bold text-sm text-gray-500 hover:bg-gray-100 transition-all"
              >
                Cancel
              </button>
              <button
                disabled={isApplyDisabled()}
                onClick={handleConfirm}
                className={`px-8 py-2 rounded-lg font-bold text-sm shadow-md transition-all ${
                  isApplyDisabled()
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-[#881337] text-white hover:bg-[#70102d]"
                }`}
              >
                Apply Changes
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BeerBulkActionModal;
